<script setup lang="ts">
/*
 * The one price rendering: bold tabular euros with lighter cents, optional
 * "from" prefix when several offers exist, optional struck MSRP. Screen
 * readers get the plain formatted amount. `hud` is the console's huge
 * tabular figure and inherits its size from the parent.
 */
import { computed } from 'vue';
import { formatEuros } from '../utils/format';

const props = withDefaults(
  defineProps<{
    cents: number;
    size?: 'sm' | 'md' | 'lg' | 'hud';
    from?: boolean;
    msrpCents?: number | null;
  }>(),
  { size: 'md', from: false, msrpCents: null },
);

const SIZE = {
  sm: { whole: 'text-[15px]', cents: 'text-[11px]' },
  md: { whole: 'text-[20px]', cents: 'text-[13px]' },
  lg: { whole: 'hud-number text-[44px]', cents: 'text-[18px]' },
  // HUD figures (cart subtotal, checkout total, wallet balance): the parent
  // sets size and colour (`text-[..px]`, `text-text-inv` on amber tiles);
  // cents scale with the size and dim by opacity, so they work on any fill.
  hud: { whole: 'hud-number text-[1em] text-current', cents: 'text-[0.4em] text-current opacity-60' },
} as const;

const hud = computed(() => props.size === 'hud');

const parts = computed(() => {
  const whole = Math.floor(props.cents / 100);
  const cents = Math.abs(props.cents % 100);
  return { whole: whole.toLocaleString('en-US'), cents: cents.toString().padStart(2, '0') };
});

const hasMsrp = computed(() => props.msrpCents != null && props.msrpCents > props.cents);
</script>

<template>
  <span class="inline-flex items-baseline gap-1.5 font-heading tabular-nums">
    <span class="sr-only">{{ formatEuros(cents) }}</span>
    <span v-if="from" aria-hidden="true" class="text-[11px] font-medium uppercase tracking-wide text-text-faint">{{ $t('common.price.from') }}</span>
    <span aria-hidden="true" class="flex items-baseline font-extrabold leading-none" :class="hud ? '' : 'text-text-base'">
      <span :class="SIZE[size].whole">{{ parts.whole }}</span>
      <span :class="[SIZE[size].cents, 'font-bold', hud ? '' : 'text-text-dim']">,{{ parts.cents }}</span>
      <span :class="[SIZE[size].cents, 'ml-0.5 font-semibold', hud ? '' : 'text-text-dim']">€</span>
    </span>
    <s v-if="hasMsrp" aria-hidden="true" class="text-[12px] text-text-faint">{{ formatEuros(msrpCents) }}</s>
  </span>
</template>
